import { useCallback, useEffect, useMemo } from 'react';
import { Observable, Subject } from 'rxjs';
import { createContext } from 'use-context-selector';
import useBusLogging from './useBusLogging';
import { ComposerEvent, ComposerValueState, useComposerState } from './useComposerState';
import { DocumentEvent, DocumentState, useDocumentState } from './useDocumentState';
import { useDraftPersistence } from './useDraftPersistence';
import { EditorEvent, EditorMachine, EditorValueState, useEditorMachine } from './useEditorMachine';
import { EditorNotificationEvent, useEditorNotifications } from './useEditorNotifications';
import { useInitialisation } from './useInitialisation';
import { useInputHandling } from './useInputHandling';
import useLocalStorageBackup, { LocalBackupState } from './useLocalStorageBackup';
import { NavigatorEvent, useNavigator } from './useNavigator';
import useOutdatedPolling from './useOutdatedPolling';
import { useSendDelete } from './useSendDelete';
import { SideCarEvent, SideCarState, useSideCar } from './useSideCar';

export type BusEvent =
  | { type: 'INITIALISE' }
  | { type: 'Editor'; event: EditorEvent }
  | { type: 'Notify'; event: EditorNotificationEvent }
  | { type: 'Navigator'; event: NavigatorEvent }
  | { type: 'SideCar'; event: SideCarEvent }
  | { type: 'Document'; event: DocumentEvent }
  | { type: 'Composer'; event: ComposerEvent };

export type BusDispatch = (event: BusEvent) => void;

export type EditorContext = {
  id: string;

  bus$: Subject<BusEvent>;
  dispatch: (event: BusEvent) => void;

  composerState: ComposerValueState;
  stateMachine: EditorMachine;

  document: DocumentState;
  sideCar: SideCarState;
  localBackup: LocalBackupState;
};

const Context = createContext<EditorContext>({} as EditorContext);

export const useEditorContext = (id: string): EditorContext => {
  // --- Global Bus and Dispatch
  const bus$ = useMemo(() => new Subject<BusEvent>(), []);
  const dispatch = useCallback((e: BusEvent) => bus$.next(e), [bus$]);

  useBusLogging(bus$);

  const stateMachine = useEditorMachine(bus$);
  const document = useDocumentState(bus$);

  const composerState = useComposerState(bus$);
  const sideCar = useSideCar(bus$);

  useInitialisation(bus$, dispatch, id);
  useEditorNotifications(bus$, document);
  useNavigator(bus$);
  useInputHandling(bus$, dispatch);

  useDraftPersistence(bus$, dispatch, document);
  const localBackup = useLocalStorageBackup(bus$, dispatch, id, document);

  useSendDelete(bus$, dispatch, id);

  useOutdatedPolling(
    bus$,
    dispatch,
    stateMachine.state$ as Observable<EditorValueState>,
    id,
    document
  );

  useEffect(() => {
    // 😈 Naughty. Allow some time for all the authed API functions to register.
    // RX is too fast :/
    const t = setTimeout(() => {
      dispatch({ type: 'INITIALISE' });
    }, 10);
    return () => clearTimeout(t);
  }, [dispatch]);

  return { id, bus$, dispatch, document, stateMachine, composerState, sideCar, localBackup };
};

export default Context;
